/**
 * Wires the outbox to the query cache, the toaster and the recovery banner.
 *
 * A component, for the same reason `SessionBridge` is one: `outbox.ts` may not import the
 * QueryClient without a cycle, and keeping it ignorant of React is what lets the whole
 * queue be tested in Jest with nothing but `fake-indexeddb`.
 *
 * **TanStack Query = what the UI believes. Outbox = what the server will eventually be
 * told.** This file is the seam between the two (`111-frontend-architecture.md` §3).
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/api/client';
import { keys } from '@/api/keys';
import type { PageDto } from '@/api/endpoints/pages';
import type { SettingsDto } from '@/api/endpoints/settings';
import { toast } from '@/components/ui/sonner';
import { translate, translatePlural, getLang } from '@/lib/i18n';
import type { OutboxEntry } from '@/offline/db';
import { findEvent as findCachedEvent } from '@/features/calendar/invalidate';
import { openEventDialog } from '@/features/calendar/eventDialogStore';
import { clearSettingsViolations, setSettingsViolations } from '@/features/settings/settingsErrors';
import { openConflict } from '@/offline/conflicts';
import { draftFromContent, loadDraft, markDraftSynced } from '@/offline/drafts';
import { outbox, setOutboxHandlers } from '@/offline/outbox';
import { requestPersistence } from '@/offline/db';

function isPageDto(value: unknown): value is PageDto {
  return typeof value === 'object' && value !== null && 'id' in value && 'updatedAt' in value;
}

/** The settings document is nested groups, never an entity — no `id`, no `updatedAt`. */
function isSettingsDto(value: unknown): value is SettingsDto {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('ai' in value || 'ui' in value || 'calendar' in value)
  );
}

/** The subset of an event write body the 422 path needs to re-open the dialog. */
interface EventBody {
  startsAt?: string;
  allDay?: boolean;
}

export function OutboxBridge({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  useEffect(() => {
    setOutboxHandlers({
      onSuccess: (entry, response) => {
        void (async () => {
          switch (entry.kind) {
            case 'page.update': {
              if (isPageDto(response)) {
                // 111 §4.4: the content save is NOT optimistic — the editor already owns
                // the text on screen. On success we take the server's copy, which is the
                // only place `updatedAt` (the next save's precondition) comes from.
                queryClient.setQueryData(keys.pages.detail(entry.entityId), response);
                await markDraftSynced(entry.entityId, response.updatedAt);
              }
              // The tree too: the title may have changed, and the tree carries titles.
              await queryClient.invalidateQueries({ queryKey: keys.pages.tree() });

              const body = entry.request.body as
                | { content?: unknown; title?: unknown; tagIds?: unknown; dueAt?: unknown }
                | undefined;

              // A due date appears in the calendar (`040`: `/api/calendar` merges events and
              // dues), so the ranges on screen are now stale. `111` §3.3: "we invalidate the
              // whole ['calendar'] prefix rather than trying to work out which ranges overlap
              // — calendar ranges are few and cheap."
              if (body !== undefined && 'dueAt' in body) {
                await queryClient.invalidateQueries({ queryKey: keys.calendar.all() });
              }

              // Two different server-side link passes can fire here, and both change OTHER
              // pages' backlink lists in ways the response cannot tell us about:
              //
              //  - `content` → `PageLinkSynchronizer` re-derived `page_link` from the
              //    document (`070` §7.3). This is what makes criterion 8 true: the target
              //    page's panel shows the source "within one save cycle".
              //  - `title`   → `PageLinkResolver` ran (`070` §7.4), so any unresolved link
              //    that was waiting on the NEW title has just resolved to this page.
              //
              // We cannot know which lists moved, so we invalidate the whole prefix
              // (`111` §3.3).
              if (body?.content !== undefined || body?.title !== undefined) {
                await queryClient.invalidateQueries({ queryKey: keys.pages.allBacklinks() });
              }

              // Tag page-counts moved, and so did the membership of the tag views.
              if (body?.tagIds !== undefined) {
                await queryClient.invalidateQueries({ queryKey: keys.tags.all() });
              }
              break;
            }

            case 'page.create':
              // The 201 carries the full page — including the REAL `updatedAt`, which is
              // the first server version this page has ever had. Writing it here is what
              // lets the editor start sending `If-Unmodified-Since` (see UNCONFIRMED).
              // A 409 replay resolves with no body; the invalidate below covers that.
              if (isPageDto(response)) {
                queryClient.setQueryData(keys.pages.detail(entry.entityId), response);
              } else {
                await queryClient.invalidateQueries({
                  queryKey: keys.pages.detail(entry.entityId),
                });
              }
              await queryClient.invalidateQueries({ queryKey: keys.pages.tree() });

              // LATE RESOLUTION (`070` §7.4). Creating a page runs `PageLinkResolver`, which
              // fills in `target_page_id` on every unresolved link that was waiting for this
              // title — possibly on pages the user has not looked at in three weeks. Their
              // backlink lists just changed. (The *documents* did not, and must not: the
              // rendered links resolve from the tree cache, which the invalidate above
              // refreshes. That is the whole trade §7.4 argues for.)
              await queryClient.invalidateQueries({ queryKey: keys.pages.allBacklinks() });
              break;

            case 'page.move':
              await queryClient.invalidateQueries({ queryKey: keys.pages.tree() });
              break;

            case 'page.archive':
              await queryClient.invalidateQueries({ queryKey: keys.pages.all() });
              break;

            case 'page.restore': {
              // The orphan rule (`060` §4): a page whose parent is STILL archived would be
              // restored into a hidden parent — live, but invisible and unreachable except
              // by search. So the server re-parents it to the root and says so. **Tell the
              // user.** Silent reparenting is how people lose things.
              const result = response as { reparented?: boolean } | undefined;
              if (result?.reparented === true) {
                toast.info(translate('offline.restoredToTop'));
              }
              await queryClient.invalidateQueries({ queryKey: keys.pages.all() });
              break;
            }

            case 'page.delete':
              queryClient.removeQueries({ queryKey: keys.pages.detail(entry.entityId) });
              await queryClient.invalidateQueries({ queryKey: keys.pages.all() });
              await queryClient.invalidateQueries({ queryKey: keys.storage() });
              break;

            case 'trash.empty': {
              // The server cascaded to the descendants and enqueued the asset GC (`062` §5), so
              // the whole `['pages']` prefix is suspect — including the DETAIL cache of a page
              // the user may still have open in another tab.
              const result = response as { deleted?: number } | undefined;
              if (typeof result?.deleted === 'number' && result.deleted > 0) {
                toast.success(
                  translatePlural(getLang(), 'offline.deletedPermanently', result.deleted),
                );
              }
              await queryClient.invalidateQueries({ queryKey: keys.pages.all() });
              await queryClient.invalidateQueries({ queryKey: keys.storage() });
              break;
            }

            case 'tag.create':
              // `070` §6: a genuine race on the case-insensitive unique index answers `409`,
              // "the client then re-fetches the tag list and reuses the existing id". The
              // outbox has already treated that 409 as success (the replay rule — the row
              // exists, which is what we wanted), and `response` is `undefined` on that path.
              // Either way the refetch below is the re-fetch the spec asks for.
              //
              // The client resolves case-insensitively BEFORE it ever offers "Create tag"
              // (`TagInput.tsx`), so reaching this with a conflict means two tabs raced —
              // rare, and self-healing on the next tag-list read.
              await queryClient.invalidateQueries({ queryKey: keys.tags.all() });
              break;

            case 'tag.update':
              // A rename requires NO page rewrites (assignment is by id) — but page payloads
              // EMBED the tag's name, so their cached copies are now stale (`111` §3.3).
              await queryClient.invalidateQueries({ queryKey: keys.tags.all() });
              await queryClient.invalidateQueries({ queryKey: keys.pages.all() });
              break;

            case 'tag.delete':
              // Cascades on `page_tag` only. The PAGES are untouched — the invalidate is to
              // drop the chip from their cached payloads, not because anything was deleted.
              await queryClient.invalidateQueries({ queryKey: keys.tags.all() });
              await queryClient.invalidateQueries({ queryKey: keys.pages.all() });
              break;

            case 'event.create':
            case 'event.update':
            case 'event.delete':
            case 'event.toggle':
              // Delivery, not enqueue, is where this belongs: the mutation resolved as soon as
              // the entry hit IndexedDB, and invalidating THEN would refetch a server copy that
              // does not yet contain the write and clobber the optimistic patch.
              //
              // The whole `['calendar']` prefix, not `111` §3.3's overlap predicate: an entry is
              // not an event (a `DELETE` and a `complete` carry no dates at all), and at most a
              // handful of ranges are ever cached. See `features/calendar/invalidate.ts`.
              await queryClient.invalidateQueries({ queryKey: keys.calendar.all() });
              // The event↔page links may have moved, in either direction (`080` §8).
              await queryClient.invalidateQueries({ queryKey: ['pages', 'events'] });
              break;

            case 'category.create':
            case 'category.update':
            case 'category.delete':
              // Events EMBED their category's name and colour, so every cached range is stale
              // (`111` §3.3). A deleted category leaves its events alive with `category: null`,
              // which is why this is an invalidate and not a cache surgery.
              await queryClient.invalidateQueries({ queryKey: keys.eventCategories() });
              await queryClient.invalidateQueries({ queryKey: keys.calendar.all() });
              break;

            case 'settings.patch':
              // `PATCH /api/settings` returns the FULL document, not the diff (`040`,
              // `100` §3) — so the response IS the cache entry. Writing it rather than
              // invalidating avoids a needless GET, and it is what re-syncs a value the
              // server normalized (or clamped) away from what we optimistically wrote.
              //
              // A `settings.patch` that succeeded means whatever the registry rejected last
              // time is no longer rejected.
              if (isSettingsDto(response)) {
                queryClient.setQueryData(keys.settings(), response);
              } else {
                await queryClient.invalidateQueries({ queryKey: keys.settings() });
              }
              clearSettingsViolations();
              break;

            default:
              break;
          }
        })();
      },

      // §7.4 — a 412 discovered by a replay is the SAME situation as a divergence
      // discovered on reload, so it opens the SAME banner. Neither version is clobbered.
      onConflict: (entry: OutboxEntry, current: PageDto) => {
        void (async () => {
          const body = entry.request.body as { content?: PageDto['content']; title?: string };
          const stored = await loadDraft(entry.entityId);

          // The queued body IS the draft, if none is stored.
          const draft =
            stored ??
            (body.content === undefined
              ? undefined
              : draftFromContent(
                  entry.entityId,
                  body.content,
                  body.title ?? current.title,
                  entry.request.ifUnmodifiedSince ?? current.updatedAt,
                ));

          if (draft === undefined) return;

          openConflict({ pageId: entry.entityId, draft, server: current, entryId: entry.id });
          toast.warning(translate('offline.pageChangedElsewhere'));
        })();
      },

      onPermanentFailure: (entry: OutboxEntry, error: ApiError) => {
        // The entry STAYS in the queue with its body intact (§5.4). This is only the
        // telling — never the dropping.
        if (error.status === 412) return; // the banner already speaks for it

        // A `422` on an event the server rejected — `endsAt <= startsAt` is the one that
        // actually happens (`080` §1.4). The dialog re-opens carrying the rejected values AND
        // the server's violations, so the message lands INLINE on the offending field rather
        // than in a toast the user cannot act on (`140` §M5, criterion 7). The failed entry is
        // handed to the dialog: saving the correction replaces it, which is a human decision
        // taken with the rejected content on screen — the only condition under which an entry
        // may leave the queue unsent (§5.4).
        if (
          error.status === 422 &&
          (entry.kind === 'event.create' || entry.kind === 'event.update')
        ) {
          const body = entry.request.body as EventBody | undefined;
          const current = findCachedEvent(queryClient, entry.entityId);

          openEventDialog({
            mode: entry.kind === 'event.create' ? 'create' : 'edit',
            // The optimistic copy is still in the cache (a permanent failure does not roll it
            // back — the mutation resolved when the entry was queued), so the form comes back
            // with the user's own values rather than an empty box.
            ...(current === undefined ? {} : { event: current }),
            ...(body?.startsAt === undefined
              ? {}
              : { draft: { dayKey: body.startsAt.slice(0, 10), allDay: body.allDay ?? true } }),
            ...(error.violations === undefined ? {} : { violations: error.violations }),
            entryId: entry.id,
          });

          toast.error(error.message, { description: translate('offline.fixEventAndSave') });
          return;
        }

        // `100-settings.md` §6: the whole PATCH is atomic — one invalid value rejects all of
        // it and NOTHING is written. So the optimistic cache is now a lie; roll it back to the
        // server's truth, and put the violation where the control can render it inline.
        if (entry.kind === 'settings.patch' && error.status === 422) {
          setSettingsViolations(
            Object.fromEntries(
              (error.violations ?? []).map((violation) => [violation.field, violation.message]),
            ),
          );
          void queryClient.invalidateQueries({ queryKey: keys.settings() });
          toast.error(translate('offline.settingSaveFailed'), { description: error.message });
          return;
        }

        if (entry.kind === 'page.move' && error.status === 409) {
          void queryClient.invalidateQueries({ queryKey: keys.pages.tree() });
          toast.error(translate('offline.pageMoveConflict'));
          return;
        }

        toast.error(translate('offline.syncChangeFailed', { message: error.message }), {
          description: translate('offline.syncChangeFailedHint'),
        });
      },
    });

    void requestPersistence();
    outbox.start();

    return () => {
      setOutboxHandlers({});
    };
  }, [queryClient]);

  return children;
}
